import { useEffect, useRef, useCallback, useMemo } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import { getTutorialSteps } from "../config/tutorialSteps";
import { getTutorialProgress, saveTutorialProgress } from "../api/api";
import { getCurrentBranchId } from "../utils/auth";

/**
 * Tutorial overlay using driver.js (React 18/19 compatible).
 * - stepIndex synced with API for dashboard tour: load on start, save on step change / skip / close.
 * - When steps prop is provided (e.g. profile tour), uses those steps and does not persist progress.
 * - Skip / Close always available via showButtons.
 */
const TutorialOverlay = ({ run, onClose, onComplete, advanceToStepRef, steps: stepsProp }) => {
  const branchId = getCurrentBranchId();
  const driverRef = useRef(null);

  // Stabilize steps so they don't cause effect re-runs on every render
  const defaultSteps = useMemo(() => getTutorialSteps(), []);
  const steps = stepsProp ?? defaultSteps;
  const stepsRef = useRef(steps);
  stepsRef.current = steps;

  const persistToApi = stepsProp == null;

  // Use refs for callbacks so the driver effect doesn't re-run when they change
  const onCloseRef = useRef(onClose);
  const onCompleteRef = useRef(onComplete);
  onCloseRef.current = onClose;
  onCompleteRef.current = onComplete;

  const persistProgress = useCallback(
    (index, completed = false) => {
      if (!persistToApi) return;
      const progress = {
        stepIndex: index,
        completedAt: completed ? new Date().toISOString() : undefined,
      };
      saveTutorialProgress(branchId, progress).catch(() => {});
    },
    [branchId, persistToApi]
  );

  // Ensure body/html are scrollable and clickable after tour (driver.js can leave body classes / overlay)
  const ensureScrollRestored = useCallback(() => {
    document.body.classList.remove("driver-active", "driver-fade", "driver-simple");
    document.documentElement.classList.remove("driver-active", "driver-fade", "driver-simple");
    document.body.style.overflow = "";
    document.body.style.pointerEvents = "";
    document.body.style.position = "";
    document.body.style.height = "";
    document.body.style.top = "";
    document.body.style.left = "";
    document.body.style.width = "";
    document.documentElement.style.overflow = "";
    document.documentElement.style.pointerEvents = "";
    document.documentElement.style.height = "";
    // Scroll to top so navbar is visible
    window.scrollTo(0, 0);
    const root = document.getElementById("root");
    if (root) {
      root.style.overflow = "";
      root.style.height = "";
      root.scrollTop = 0;
    }
    // Clear highlight classes from any previously highlighted elements
    document.querySelectorAll(".driver-active-element, .driver-no-interaction").forEach((el) => {
      el.classList.remove("driver-active-element", "driver-no-interaction");
    });
    // Remove any leftover driver.js overlay/popover nodes safely
    document.querySelectorAll(".driver-overlay, .driver-overlay-animated, .driver-popover, #driver-popover-content").forEach((el) => {
      try { if (el.parentNode) el.parentNode.removeChild(el); } catch (_) {}
    });
    try {
      const dummy = document.getElementById("driver-dummy-element");
      if (dummy && dummy.parentNode) dummy.parentNode.removeChild(dummy);
    } catch (_) {}
  }, []);

  // Start tour when run becomes true; destroy when run becomes false
  // Only depends on `run`, `branchId`, and `persistToApi` — callbacks use refs so they don't trigger re-runs
  useEffect(() => {
    if (!run) {
      if (driverRef.current?.isActive?.()) {
        driverRef.current.destroy();
      }
      ensureScrollRestored();
      driverRef.current = null;
      setTimeout(ensureScrollRestored, 100);
      setTimeout(ensureScrollRestored, 250);
      return;
    }

    let cancelled = false;
    const currentSteps = stepsRef.current;

    const startDriver = (startIndex = 0) => {
      if (cancelled) return;
      const driverObj = driver({
        steps: currentSteps,
        showProgress: true,
        showButtons: ["next", "previous", "close"],
        allowClose: true,
        overlayColor: "rgba(0,0,0,0.6)",
        smoothScroll: true,
        stagePadding: 8,
        stageRadius: 8,
        animate: true,
        onHighlighted: (_, step, opts) => {
          const idx = opts.driver.getActiveIndex();
          if (typeof idx === "number") persistProgress(idx);
        },
        onDestroyStarted: (element, step, opts) => {
          const idx = opts.driver.getActiveIndex();
          const isLast = opts.driver.isLastStep();
          const completed = isLast && typeof idx === "number";
          // When completed, reset progress so next tour starts from step 0
          persistProgress(completed ? 0 : (idx ?? 0), completed);
          onCompleteRef.current?.();
          onCloseRef.current?.();
          opts.driver.destroy();
          ensureScrollRestored();
          requestAnimationFrame(() => {
            ensureScrollRestored();
            requestAnimationFrame(() => {
              ensureScrollRestored();
              window.scrollTo(0, 0);
              setTimeout(ensureScrollRestored, 150);
            });
          });
        },
      });
      driverRef.current = driverObj;
      driverObj.drive(startIndex);
    };

    if (persistToApi) {
      getTutorialProgress(branchId)
        .then((res) => {
          if (cancelled) return;
          const startIndex =
            res.success && res.progress && typeof res.progress.stepIndex === "number"
              ? Math.min(Math.max(0, res.progress.stepIndex), currentSteps.length - 1)
              : 0;
          startDriver(startIndex);
        })
        .catch(() => {
          if (cancelled) return;
          startDriver(0);
        });
    } else {
      startDriver(0);
    }

    return () => {
      cancelled = true;
      if (driverRef.current?.isActive?.()) {
        driverRef.current.destroy();
      }
      ensureScrollRestored();
      driverRef.current = null;
      setTimeout(ensureScrollRestored, 100);
      setTimeout(ensureScrollRestored, 250);
    };
  }, [run, branchId, persistToApi, persistProgress, ensureScrollRestored]);

  // Expose advanceToStep for parent (e.g. advance when user performs real action)
  useEffect(() => {
    if (!advanceToStepRef) return;
    advanceToStepRef.current = (index) => {
      if (!driverRef.current?.isActive?.()) return;
      const currentSteps = stepsRef.current;
      const next = Math.max(0, Math.min(index, currentSteps.length - 1));
      driverRef.current.moveTo(next);
      persistProgress(next);
    };
    return () => {
      advanceToStepRef.current = null;
    };
  }, [advanceToStepRef, persistProgress]);

  return null;
};

export default TutorialOverlay;
